<script>
    import { createEventDispatcher } from "svelte";
    import {
        salesSearch,
        appliedSalesSearch,
        salesPlanWeeks,
        salesModelIntegrations,
        BASE_YEAR_OPTIONS,
        fetchSalesStatusData,
        isSalesLoading,
        exportSalesGridToCSV,
        toggleYearEndExtend,
        yearStartDate,
        yearEndDate
    } from "./store.js";
    import MondayDatePicker from "../../components/MondayDatePicker.svelte";

    export let openTreeModal;
    export let openColumnModal;
    export let openMngmtUploadModal;

    const dispatch = createEventDispatcher();

    const PERF_TYPES = [
        { value: "SHIPMENT", label: "출하" },
        { value: "SALES", label: "판매" },
    ];
    const UNIT_TYPES = [
        { value: "QTY", label: "대" },
        { value: "AMOUNT", label: "금액" },
    ];
    const PERIOD_TYPES = [
        { value: "WEEK", label: "주" },
        { value: "MONTH", label: "월" },
        { value: "QUARTER", label: "분기" },
    ];
    const MONTH_BASE_TYPES = [
        { value: "WEEK", label: "주차" },
        { value: "DAY", label: "일" },
    ];

    function setField(key, value) {
        salesSearch.update(s => ({ ...s, [key]: value }));
    }

    // 기준년도 변경 시 조회구간을 해당 연도 전체(1/1 ~ 12/31)로 초기화
    function handleBaseYearChange(e) {
        const y = e.target.value;
        salesSearch.update(s => ({
            ...s,
            baseYear: y,
            startDate: yearStartDate(y),
            endDate: yearEndDate(y),
        }));
    }

    // 조회구간 시작일/종료일 변경 (시작 > 종료가 되지 않도록 보정)
    // 입력 도중의 불완전한 값은 MondayDatePicker 가 걸러서 완성된 유효 날짜만 전달됨
    function handleStartDateChange(v) {
        if (!v) return;
        salesSearch.update(s => ({ ...s, startDate: v, endDate: s.endDate && s.endDate < v ? v : s.endDate }));
    }

    function handleEndDateChange(v) {
        if (!v) return;
        salesSearch.update(s => ({ ...s, endDate: v, startDate: s.startDate && s.startDate > v ? v : s.startDate }));
    }

    // 조회구간 "전체" 버튼 표시 여부 (조회구간 요건이 확정되기 전까지 숨김, true 로 바꾸면 다시 표시)
    const SHOW_RANGE_RESET_BTN = false;

    function resetDateRange() {
        salesSearch.update(s => ({ ...s, startDate: yearStartDate(s.baseYear), endDate: yearEndDate(s.baseYear) }));
    }

    $: isFullRange = $salesSearch.startDate === yearStartDate($salesSearch.baseYear)
        && $salesSearch.endDate === yearEndDate($salesSearch.baseYear);

    // 제품 트리 선택 요약 (선택 없음 = 전체)
    $: treeCount = ($salesSearch.selectedItemGroups || []).length + ($salesSearch.selectedGroupModels || []).length;

    // 조회 조건을 바꿔도 [조회하기]를 누르기 전에는 표가 바뀌지 않음 → 적용된 조건과 다르면 조회버튼 강조
    // (년말확장은 표시 토글이라 즉시 반영되므로 비교 대상에서 제외)
    const CHECK_KEYS = [
        "planWeek", "baseYear", "startDate", "endDate", "modelIntegration", "itemCode",
        "perfType", "unitType", "periodType", "monthBaseType", "excludeZero",
    ];
    const sameList = (a, b) => (a || []).join(",") === (b || []).join(",");
    $: isConditionChanged =
        CHECK_KEYS.some(k => $salesSearch[k] !== $appliedSalesSearch[k]) ||
        !sameList($salesSearch.selectedItemGroups, $appliedSalesSearch.selectedItemGroups) ||
        !sameList($salesSearch.selectedGroupModels, $appliedSalesSearch.selectedGroupModels);

    async function handleSearch() {
        await fetchSalesStatusData();
        dispatch("search");
    }
</script>

<div class="search-area">
    {#if $salesSearch.isExpanded !== false}
        <section class="search-panel">
            <!-- 1행: 편성주차, 기준시점, 제품품번, 0 제외 -->
            <div class="search-grid search-grid-row1">
                <div class="field">
                    <label>편성주차</label>
                    <select
                        value={$salesSearch.planWeek}
                        on:change={(e) => setField("planWeek", e.target.value)}
                    >
                        {#if $salesPlanWeeks.length === 0}
                            <option value="">편성주차 목록 없음</option>
                        {/if}
                        {#each $salesPlanWeeks as pw, i}
                            <option value={pw}>{pw}{i === 0 ? " (최신)" : ""}</option>
                        {/each}
                    </select>
                </div>

                <div class="field">
                    <label>기준년도</label>
                    <select
                        value={$salesSearch.baseYear}
                        on:change={handleBaseYearChange}
                    >
                        {#each BASE_YEAR_OPTIONS as y}
                            <option value={String(y)}>{y}년</option>
                        {/each}
                    </select>
                </div>

                <div class="field date-range-field">
                    <div class="field-title-row">
                        <label>조회구간</label>
                        {#if SHOW_RANGE_RESET_BTN}
                            <button
                                type="button"
                                class="preset-btn"
                                class:active={isFullRange}
                                on:click={resetDateRange}
                                title="기준년도 전체 (1/1 ~ 12/31)"
                            >전체</button>
                        {/if}
                    </div>
                    <div class="date-range-inputs">
                        <div class="date-input-wrap">
                            <MondayDatePicker
                                compact
                                value={$salesSearch.startDate}
                                min={yearStartDate($salesSearch.baseYear)}
                                max={yearEndDate($salesSearch.baseYear)}
                                on:change={(e) => handleStartDateChange(e.detail)}
                            />
                        </div>
                        <span class="range-sep">~</span>
                        <div class="date-input-wrap">
                            <MondayDatePicker
                                compact
                                value={$salesSearch.endDate}
                                min={yearStartDate($salesSearch.baseYear)}
                                max={yearEndDate($salesSearch.baseYear)}
                                on:change={(e) => handleEndDateChange(e.detail)}
                            />
                        </div>
                    </div>
                </div>

                <div class="field">
                    <label>기종통합</label>
                    <select
                        value={$salesSearch.modelIntegration}
                        on:change={(e) => setField("modelIntegration", e.target.value)}
                    >
                        <option value="">전체 (ALL)</option>
                        {#each $salesModelIntegrations as mi}
                            <option value={mi.code}>{mi.name || mi.code}</option>
                        {/each}
                    </select>
                </div>

                <div class="field">
                    <label>제품품번</label>
                    <input
                        type="text"
                        placeholder="품번, 품명, 그룹 입력"
                        bind:value={$salesSearch.itemCode}
                        on:keydown={(e) => e.key === "Enter" && handleSearch()}
                    />
                </div>

                <div class="field">
                    <label class="invisible-label">&nbsp;</label>
                    <div class="checkbox-group">
                        <label class="checkbox-label" class:checked={$salesSearch.excludeZero}>
                            <input type="checkbox" bind:checked={$salesSearch.excludeZero} />
                            <span>0 제외</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- 2행: 실적유형, 집계단위, 구간단위, 월편성기준, 도구버튼 -->
            <div class="search-grid search-grid-row2">
                <div class="field">
                    <label>실적유형 <span class="label-hint">출하-납기, 판매-계산서</span></label>
                    <div class="segment-group">
                        {#each PERF_TYPES as opt}
                            <button
                                type="button"
                                class="segment-btn"
                                class:checked={$salesSearch.perfType === opt.value}
                                disabled={opt.disabled}
                                title={opt.hint || ""}
                                on:click={() => setField("perfType", opt.value)}
                            >{opt.label}</button>
                        {/each}
                    </div>
                </div>

                <div class="field">
                    <label>집계단위</label>
                    <div class="segment-group">
                        {#each UNIT_TYPES as opt}
                            <button
                                type="button"
                                class="segment-btn"
                                class:checked={$salesSearch.unitType === opt.value}
                                on:click={() => setField("unitType", opt.value)}
                            >{opt.label}</button>
                        {/each}
                    </div>
                </div>

                <div class="field">
                    <label>구간단위</label>
                    <div class="segment-group">
                        {#each PERIOD_TYPES as opt}
                            <button
                                type="button"
                                class="segment-btn"
                                class:checked={$salesSearch.periodType === opt.value}
                                on:click={() => setField("periodType", opt.value)}
                            >{opt.label}</button>
                        {/each}
                    </div>
                </div>

                <div class="field">
                    <label>월편성기준</label>
                    <div class="segment-group">
                        {#each MONTH_BASE_TYPES as opt}
                            <button
                                type="button"
                                class="segment-btn"
                                class:checked={$salesSearch.monthBaseType === opt.value}
                                on:click={() => setField("monthBaseType", opt.value)}
                            >{opt.label}</button>
                        {/each}
                    </div>
                </div>

                <div class="field tool-buttons-container">
                    <label class="invisible-label">&nbsp;</label>
                    <div class="tool-btn-group">
                        <button type="button" class="tool-btn" on:click={openTreeModal} title="제품 분류 트리 열기 (제품유형 ➔ 품목그룹 ➔ 기종)">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                                <polyline points="2 17 12 22 22 17"/>
                                <polyline points="2 12 12 17 22 12"/>
                            </svg>
                            트리선택
                            {#if treeCount > 0}<span class="tree-badge">{treeCount}건</span>{/if}
                        </button>
                        <button type="button" class="tool-btn" on:click={openColumnModal} title="컬럼 구성 및 표시 설정">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="4" y1="21" x2="4" y2="14"/>
                                <line x1="4" y1="10" x2="4" y2="3"/>
                                <line x1="12" y1="21" x2="12" y2="12"/>
                                <line x1="12" y1="8" x2="12" y2="3"/>
                                <line x1="20" y1="21" x2="20" y2="16"/>
                                <line x1="20" y1="12" x2="20" y2="3"/>
                                <line x1="1" y1="14" x2="7" y2="14"/>
                                <line x1="9" y1="8" x2="15" y2="8"/>
                                <line x1="17" y1="16" x2="23" y2="16"/>
                            </svg>
                            컬럼설정
                        </button>
                        <button
                            type="button"
                            class="tool-btn year-end-btn"
                            class:active={$salesSearch.yearEndExtend}
                            on:click={toggleYearEndExtend}
                            title="조회구간 오른쪽에 차년 +3개월 데이터를 함께 표시 (다시 누르면 해제)"
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3">
                                <polyline points="9 18 15 12 9 6"/>
                                <polyline points="15 18 21 12 15 6"/>
                            </svg>
                            년말확장
                        </button>
                        <button type="button" class="tool-btn" on:click={openMngmtUploadModal} title="경영계획(경영) 엑셀 업로드">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            경영 업로드
                        </button>
                        <button type="button" class="tool-btn excel-tool-btn" on:click={exportSalesGridToCSV} title="엑셀 CSV 다운로드">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            엑셀
                        </button>
                    </div>
                </div>
            </div>

            <!-- 데이터 조회하기 메인 버튼 -->
            <button
                type="button"
                class="main-btn"
                class:highlight-search={isConditionChanged}
                on:click={handleSearch}
                disabled={$isSalesLoading}
            >
                {#if $isSalesLoading}
                    데이터 조회 중...
                {:else if isConditionChanged}
                    🔍 조건 변경됨 — 클릭하여 조회하기
                {:else}
                    🔍 조회하기
                {/if}
            </button>
        </section>
    {/if}

    <!-- 조회 조건 접기/펼치기 토글 바 -->
    <div
        class="toggle-bar"
        on:click={() => salesSearch.update(s => ({ ...s, isExpanded: s.isExpanded === false }))}
    >
        <div class="toggle-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <path d={$salesSearch.isExpanded !== false ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} />
            </svg>
            <span>
                {$salesSearch.isExpanded !== false ? "조회 조건 접기" : "조회 조건 펼쳐서 다시 검색하기"}
            </span>
        </div>
    </div>
</div>

<style>
    .search-area {
        flex-shrink: 0;
        background: white;
        border-bottom: 1px solid #e2e8f0;
        padding: 15px 15px 0 15px;
    }
    .search-panel {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-bottom: 10px;
    }
    .search-grid {
        display: grid;
        gap: 10px;
    }
    .search-grid-row1 {
        grid-template-columns: 1.1fr 0.75fr 2.1fr 1.1fr 1.2fr 0.7fr;
    }
    .search-grid-row2 {
        grid-template-columns: 0.9fr 0.8fr 1fr 0.8fr 3.4fr;
    }
    .field > label {
        display: block;
        font-size: 0.7rem;
        font-weight: 800;
        color: #64748b;
        margin-bottom: 4px;
    }
    .label-hint {
        font-weight: 600;
        color: #94a3b8;
        margin-left: 4px;
    }
    .field input:not([type="checkbox"]),
    .field select {
        width: 100%;
        height: 35px;
        border: 1.5px solid #e2e8f0;
        border-radius: 4px;
        padding: 0 8px;
        font-size: 0.85rem;
        box-sizing: border-box;
        background-color: #fff;
        color: #1e293b;
    }
    .field input:focus,
    .field select:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    .invisible-label {
        visibility: hidden;
    }

    /* 체크박스 / 세그먼트(라디오) 그룹 */
    .checkbox-group,
    .segment-group {
        display: flex;
        align-items: center;
        width: 100%;
        height: 35px;
        border: 1.5px solid #e2e8f0;
        border-radius: 4px;
        background: #ffffff;
        box-sizing: border-box;
        overflow: hidden;
    }
    .checkbox-label,
    .segment-btn {
        display: flex !important;
        align-items: center;
        justify-content: center;
        font-size: 0.82rem;
        font-weight: 700;
        color: #475569;
        cursor: pointer;
        user-select: none;
        flex: 1;
        height: 100%;
        margin: 0 !important;
        padding: 0 4px;
        background: #ffffff;
        border: none;
        transition: all 0.2s ease;
        position: relative;
    }
    .segment-btn:not(:last-child) {
        border-right: 1.5px solid #e2e8f0;
    }
    .checkbox-label:not(.checked):hover,
    .segment-btn:not(.checked):hover {
        background: #f8fafc;
        color: #1e293b;
    }
    /* 비활성(준비중) 옵션: 회색 + 클릭 불가 */
    .segment-btn:disabled,
    .segment-btn:disabled:hover {
        background: #f1f5f9;
        color: #b6c0cd;
        cursor: not-allowed;
    }
    .checkbox-label.checked,
    .segment-btn.checked {
        background: #3b82f6;
        color: #ffffff;
    }
    .checkbox-label input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    }

    /* 보조 툴 버튼 그룹 */
    .tool-btn-group {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 35px;
    }
    .tool-btn {
        flex: 1;
        height: 35px;
        border: 1.5px solid #e2e8f0;
        border-radius: 4px;
        background: #ffffff;
        color: #475569;
        font-size: 0.76rem;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        transition: all 0.2s;
        padding: 0 4px;
        white-space: nowrap;
    }
    .tool-btn:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #1e293b;
    }
    .tree-badge {
        font-size: 0.64rem;
        font-weight: 800;
        background: #dbeafe;
        color: #1d4ed8;
        padding: 1px 5px;
        border-radius: 8px;
    }

    /* 조회구간 (시작일 ~ 종료일) */
    /* 라벨 줄 높이를 다른 칸의 라벨(글자 + 하단 여백 4px)과 동일하게 맞춰 입력창 정렬 유지 */
    .field-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 17px;
        margin-bottom: 4px;
    }
    .field-title-row > label {
        display: block;
        font-size: 0.7rem;
        font-weight: 800;
        color: #64748b;
        margin-bottom: 0;
        line-height: 1.2;
    }
    .date-range-inputs {
        display: flex;
        align-items: center;
        gap: 3px;
    }
    .date-input-wrap {
        flex: 1;
        min-width: 0;
    }
    .range-sep {
        font-size: 0.75rem;
        color: #94a3b8;
        font-weight: 700;
    }
    .preset-btn {
        background: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        padding: 0 7px;
        height: 16px;
        font-size: 0.66rem;
        font-weight: 700;
        color: #475569;
        cursor: pointer;
        line-height: 1;
    }
    .preset-btn:hover {
        background: #e2e8f0;
    }
    .preset-btn.active,
    .preset-btn.active:hover {
        background: #2563eb;
        color: #ffffff;
        border-color: #1d4ed8;
    }

    /* 년말확장 토글 버튼 (활성 시 파란색) */
    .year-end-btn.active,
    .year-end-btn.active:hover {
        background: #2563eb;
        border-color: #1d4ed8;
        color: #ffffff;
        box-shadow: 0 1px 3px rgba(37, 99, 235, 0.4);
    }
    .excel-tool-btn {
        color: #059669;
        border-color: #a7f3d0;
        background: #f0fdf4;
    }
    .excel-tool-btn:hover {
        background: #dcfce7;
        border-color: #86efac;
    }

    /* 메인 버튼 */
    .main-btn {
        width: 100%;
        height: 40px;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 4px;
        font-weight: 800;
        margin-top: 10px;
        cursor: pointer;
        font-size: 0.95rem;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
    }
    .main-btn:hover:not(:disabled) {
        background: #2563eb;
    }
    .main-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    .main-btn.highlight-search {
        background: #1d4ed8;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.35);
        animation: pulseSearch 1.8s infinite;
    }
    @keyframes pulseSearch {
        0%, 100% {
            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.4);
        }
        50% {
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.15);
        }
    }

    /* 토글 바 */
    .toggle-bar {
        padding: 8px 0;
        background: #ffffff;
        cursor: pointer;
        display: flex;
        justify-content: center;
        border-top: 1px solid #f1f5f9;
    }
    .toggle-content {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #3b82f6;
        font-size: 0.85rem;
        font-weight: 800;
    }

    @media (max-width: 1100px) {
        .search-grid-row1,
        .search-grid-row2 {
            grid-template-columns: repeat(2, 1fr);
        }
    }
</style>
